// Redux Toolkit slice for the Releases page. Holds the release list, the CTPL
// versions snapshot, tenant name/type enrichment, async status, and the
// list-screen UI state (edit mode + rail search).
//
// No sample-data fallback: when the backend is unreachable the thunk REJECTS and
// the page shows a skeleton (loading) then an error state — it must never render
// fabricated releases/tenants (that would mislead operators and, for a denied
// user, surface data they shouldn't see).

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { IRelease, IReleaseTenantMeta, IReleaseVersions } from "../models/interfaces";
import { getReleases, getReleaseVersions } from "../serviceHelper";
import { getTenants } from "src/pages/Tenants/serviceHelper";
import { transformTenantsResponse } from "src/pages/Tenants/transformer";
import { buildTenantMeta } from "../transformer";

interface ReleasesState {
	releases: IRelease[];
	versions: IReleaseVersions | null;
	tenantsMeta: Record<string, IReleaseTenantMeta>;
	isLoading: boolean;
	error: string | null;
	editMode: boolean;
	railSearch: string;
}

export const initialState: ReleasesState = {
	releases: [],
	versions: null,
	tenantsMeta: {},
	isLoading: false,
	error: null,
	editMode: false,
	railSearch: "",
};

interface IFetchedReleaseData {
	versions: IReleaseVersions;
	releases: IRelease[];
	tenantsMeta: Record<string, IReleaseTenantMeta>;
}

// Load the versions snapshot + all releases. Tenant names/types (GET /tenants)
// are a best-effort enrichment — their failure alone doesn't fail the load
// (release records still carry tenant_name). A failure of the releases/versions
// calls themselves rejects the thunk so the page can show an error, not mock data.
export const fetchReleaseData = createAsyncThunk<IFetchedReleaseData>("releases/fetchReleaseData", async () => {
	const [versions, releases] = await Promise.all([getReleaseVersions(), getReleases()]);
	let tenants: { id?: string; tenant_code?: string; tenant_name?: string; tenant_type?: string }[] = [];
	try {
		tenants = transformTenantsResponse(await getTenants());
	} catch {
		tenants = [];
	}
	return {
		versions,
		releases: Array.isArray(releases) ? releases : [],
		tenantsMeta: buildTenantMeta(tenants, releases),
	};
});

const releasesSlice = createSlice({
	name: "releasesSlice",
	initialState,
	reducers: {
		setEditMode: (state, action: PayloadAction<boolean>) => {
			state.editMode = action.payload;
		},
		setRailSearch: (state, action: PayloadAction<string>) => {
			state.railSearch = action.payload;
		},
		// Insert or replace one release (used after POST/PATCH responses).
		upsertRelease: (state, action: PayloadAction<IRelease>) => {
			const release = action.payload;
			if (!release?.id) return;
			const idx = state.releases.findIndex((rel) => rel.id === release.id);
			if (idx >= 0) state.releases[idx] = release;
			else state.releases.push(release);
		},
		// Drop a cancelled plan from the board (the backend soft-deletes it).
		removeRelease: (state, action: PayloadAction<string>) => {
			state.releases = state.releases.filter((rel) => rel.id !== action.payload);
		},
		// Apply a completed release from POST /releases/{id}/complete: swap the
		// record AND bump the tenant's displayed bundle locally, so the rail
		// updates without waiting for a full refetch of the secret snapshot.
		applyCompletedRelease: (state, action: PayloadAction<IRelease>) => {
			const release = action.payload;
			if (!release?.id) return;
			const idx = state.releases.findIndex((rel) => rel.id === release.id);
			if (idx >= 0) state.releases[idx] = release;
			else state.releases.push(release);
			if (state.versions && release.tenant_id && release.to_version) {
				if (!state.versions.tenants) state.versions.tenants = {};
				state.versions.tenants[release.tenant_id] = {
					bundle_version: release.to_version,
					deployed_at: release.completed_at ?? release.updated_at ?? null,
				};
			}
		},
		resetReleases: () => initialState,
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchReleaseData.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchReleaseData.fulfilled, (state, action) => {
				state.isLoading = false;
				state.error = null;
				state.releases = action.payload.releases;
				state.versions = action.payload.versions;
				state.tenantsMeta = action.payload.tenantsMeta;
			})
			.addCase(fetchReleaseData.rejected, (state, action) => {
				// The backend is unreachable/erroring — surface an error and keep the
				// board empty. Never fall back to fabricated data.
				state.isLoading = false;
				state.releases = [];
				state.versions = null;
				state.tenantsMeta = {};
				state.error = action.error.message ?? "Failed to load releases";
			});
	},
});

export const { applyCompletedRelease, removeRelease, resetReleases, setEditMode, setRailSearch, upsertRelease } =
	releasesSlice.actions;
export default releasesSlice.reducer;
